import leaseCompsReducer from "@/features/survey/leaseCompsSlice";
import poisReducer from "@/features/survey/poisSlice";
import userReducer from "@/features/auth/userSlice";
import { authentication } from "@/services/authentication";
import { mediaApi, surveyApi, userApi } from "@/services/base";
import { bingApi } from "@/services/bingApi";
import toastReducer from "@components/toast/toastSlice";
import type { Action, ThunkAction } from "@reduxjs/toolkit";
import { combineSlices, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import surveyReducer from "@survey/surveySlice";

// Use reducerPath directly for each API
const rootReducer = combineSlices({
  [surveyApi.reducerPath]: surveyApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [mediaApi.reducerPath]: mediaApi.reducer,
  [bingApi.reducerPath]: bingApi.reducer,
  [authentication.reducerPath]: authentication.reducer,
  user: userReducer,
  survey: surveyReducer,
  leaseComps: leaseCompsReducer,
  pois: poisReducer,
  toast: toastReducer,
});

// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof rootReducer>;

// The store setup is wrapped in `makeStore` to allow reuse
// when setting up tests that need the same store config
export const makeStore = (preloadedState?: Partial<RootState>) => {
  const store = configureStore({
    reducer: rootReducer,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) => {
      return getDefaultMiddleware({
        serializableCheck: {
          // Ignore these action types
          ignoredActions: [
            // Ignore RTK Query action types that might contain non-serializable data
            "mediaApi/executeQuery/fulfilled",
            "mediaApi/executeQuery/pending",
            "mediaApi/executeQuery/rejected",
          ],
          // Ignore these paths in the state - matches the exact error path pattern
          ignoredPaths: [
            // Match any path that contains getEdpTransformedImage with dynamic query params
            /mediaApi\.queries\.getEdpTransformedImage\(.+\)\.data/,
            // Also match the broader path
            /mediaApi\.queries\.getEdpTransformedImage/,
          ],
        },
      })
        .concat(surveyApi.middleware)
        .concat(userApi.middleware)
        .concat(mediaApi.middleware)
        .concat(bingApi.middleware)
        .concat(authentication.middleware);
    },
    preloadedState,
  });
  // configure listeners using the provided defaults
  // optional, but required for `refetchOnFocus`/`refetchOnReconnect` behaviors
  setupListeners(store.dispatch);
  return store;
};

export const store = makeStore();

// Infer the type of `store`
export type AppStore = typeof store;
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
